import { BusyIndicatorSize } from "@components/busyIndicator/BusyIndicator.utils";
import {
    CompanyPermissionEntity,
    CompanyRoleEntity,
    EntitySetName,
    GeneralRoleEntity,
    ICompanyRolePermissionEntity,
    IGeneralRolePermissionEntity,
    UserCompanyRoleEntity,
    UserEntity
} from "@odata/GeneratedEntityTypes";
import {
    CompanyPermissionCode,
    CompanyRoleTypeCode,
    GeneralPermissionCode,
    WebSocketMessageTypeCode
} from "@odata/GeneratedEnums";
import { isBatchResultOk } from "@odata/OData";
import { WithOData, withOData } from "@odata/withOData";
import { isUserCustomer } from "@pages/admin/users/Users.utils";
import { isNotDefined } from "@utils/general";
import { logger } from "@utils/log";
import { memoizeOne } from "@utils/memoizeOne";
import React from "react";

import BusyIndicator from "../../components/busyIndicator";
import WebsocketManager from "../../utils/websocketManager/WebsocketManager";
import { AppContext, ContextEvents, IAppContext } from "../appContext/AppContext.types";
import { WithAuthContext, withAuthContext } from "../authContext/withAuthContext";

export const PermissionContext = React.createContext<IPermissionContext>(undefined);

export type TCompanyPermissions = Set<CompanyPermissionCode>;
export type TGeneralPermissions = Set<GeneralPermissionCode>;

export interface IPermissionContext {
    companyPermissions: TCompanyPermissions;
    ownOnlyPermissions: TCompanyPermissions;
    generalPermissions: TGeneralPermissions;
    hasCompanyPermissions: (permissions: CompanyPermissionCode | CompanyPermissionCode[]) => boolean;
    hasGeneralPermissions: (permissions: GeneralPermissionCode | GeneralPermissionCode[]) => boolean;
    refreshUserPermissions: () => Promise<void>;
    userIsCustomer: boolean; // true if user is customer, false if user is admin or other role
}

type TCompaniesPermissions = Record<number | string, TCompanyPermissions>;
type TOwnOnlyPermissions = Record<number | string, TCompanyPermissions>;

interface IState extends Pick<IPermissionContext, "generalPermissions"> {
    companiesPermissions: TCompaniesPermissions;
    ownOnlyPermissions: TOwnOnlyPermissions;
    isCustomer?: boolean,
    availableCompanyPermissions?: Set<CompanyPermissionCode>,
    isLoaded: boolean;
}

interface IProps extends WithOData, WithAuthContext {
    onPermissionsLoaded: (companyPermissions: Record<string, Set<CompanyPermissionCode>>, generalPermissions: Set<GeneralPermissionCode>) => void;
}

class PermissionContextProvider extends React.PureComponent<IProps, IState> {
    static contextType = AppContext;

    state: IState = {
        companiesPermissions: {},
        ownOnlyPermissions: {},
        generalPermissions: new Set(),
        isLoaded: false
    };

    _unsubscribeWebsocket: () => void;
    getCurrentCompanyPermissions = memoizeOne((): Set<CompanyPermissionCode> => {
        return this.state.companiesPermissions[this.context.getCompanyId()] ?? new Set();
    }, () => [this.context.getCompanyId(), this.state.companiesPermissions]);

    getCurrentOwnOnlyPermissions = memoizeOne((): Set<CompanyPermissionCode> => {
        return this.state.ownOnlyPermissions[this.context.getCompanyId()] ?? new Set();
    }, () => [this.context.getCompanyId(), this.state.ownOnlyPermissions]);

    constructor(props: IProps, context: IAppContext) {
        super(props, context);
        context.eventEmitter.on(ContextEvents.CompanyChanged, this.companyChange);

        this.refreshUserPermissions = this.refreshUserPermissions.bind(this);
    }

    hasCompanyPermissions = (permissions: CompanyPermissionCode | CompanyPermissionCode[]): boolean => {
        if (isNotDefined(permissions)) {
            return true; // no permissions specified, so we assume all permissions are granted
        }
        let permissionsToCheck = Array.isArray(permissions) ? permissions : [permissions];
        permissionsToCheck = permissionsToCheck.filter(p => this.state.availableCompanyPermissions?.has(p));
        if (!permissionsToCheck.length) {
            return false; // no valid permissions to check, user has different role, so he is not allowed to access this
        }
        return permissionsToCheck.every(permission => this.getCurrentCompanyPermissions()?.has(permission));
    };

    hasGeneralPermissions = (permissions: GeneralPermissionCode | GeneralPermissionCode[]): boolean => {
        if (isNotDefined(permissions)) {
            return true; // no permissions specified, so we assume all permissions are granted
        }
        const permissionsToCheck = Array.isArray(permissions) ? permissions : [permissions];
        if (!permissionsToCheck.length) {
            return false; // no valid permissions to check, user has different role, so he is not allowed to access this
        }
        return permissionsToCheck.every(permission => this.state.generalPermissions?.has(permission));
    };

    componentDidMount() {
        if (this.props.authContext && this.props.oData) {
            this.refreshUserPermissions();
        }

        this._unsubscribeWebsocket = WebsocketManager.subscribe({
            callback: this.handleSessionInvalidated,
            types: [WebSocketMessageTypeCode.SessionInvalidated]
        });
    }

    componentDidUpdate(prevProps: Readonly<IProps>, prevState: Readonly<IState>, snapshot?: any) {
        if ((this.props.authContext && this.props.oData) && (!prevProps.oData || !prevProps.authContext)) {
            this.refreshUserPermissions();
        }
    }

    componentWillUnmount(): void {
        this.context.eventEmitter.off(ContextEvents.CompanyChanged, this.companyChange);
        this._unsubscribeWebsocket?.();
    }

    companyChange = (id: number) => {
        this.refreshUserPermissions(true);
    };

    /** Retrieves and stores all General and Company permissions for current user */
    refreshUserPermissions = async (withoutFetch?: boolean) => {
        if (withoutFetch && this.state.isLoaded) {
            // we don't always need to fetch data again,
            // e.g. when changing company, it unnecessarily delays menu items re-render
            this.props.onPermissionsLoaded(this.state.companiesPermissions, this.state.generalPermissions);

            return;
        }

        const companiesPermissions: TCompaniesPermissions = {};
        const ownOnlyPermissions: TOwnOnlyPermissions = {};
        const generalPermissions: Set<GeneralPermissionCode> = new Set();
        let isCustomer: boolean;
        const availableCompanyPermissions = new Set<CompanyPermissionCode>();

        const batch = this.props.oData.batch();
        batch.beginAtomicityGroup("QueryPermissionSettings");

        batch.getEntitySetWrapper(EntitySetName.Users)
            .query(this.props.authContext.userId?.toString())
                .expand(UserEntity.GeneralRole,
                        q => q.expand(GeneralRoleEntity.GeneralRolePermissions))
            .expand(UserEntity.CompanyRoles,
                q => q.expand(UserCompanyRoleEntity.CompanyRole,
                    q => q.expand(CompanyRoleEntity.CompanyRolePermissions))
                    .expand(UserCompanyRoleEntity.Company, q => q.select(UserCompanyRoleEntity.Id))
            );

        batch.getEntitySetWrapper(EntitySetName.CompanyPermissions)
                .query()
                .select(CompanyPermissionEntity.Code, CompanyPermissionEntity.CompanyRoleTypeCode);

        // most odata requests will fail for canceled tenant
        // but, we need to try to fetch the new data in case user just restored the tenant, but the new tenant data have not yet been fetched
        try {
            const [userDataRes, permResult] = await batch.execute();
            if (!isBatchResultOk(userDataRes) || !isBatchResultOk(permResult)) {
                logger.error("Failed to fetch user permissions or company permissions.");
                return;
            }
            const user = userDataRes.body.value;
            const allCompanyPermissions = permResult.body.value;

            for (const role of (user.CompanyRoles || [])) {
                const companyPermissions: Set<CompanyPermissionCode> = new Set();
                const companyOwnOnlyPermissions: Set<CompanyPermissionCode> = new Set();
                const perms = role.CompanyRole.CompanyRolePermissions.filter((p: ICompanyRolePermissionEntity) => p.IsEnabled);

                for (const p of perms) {
                    companyPermissions.add(p.CompanyPermissionCode as CompanyPermissionCode);
                    if (p.OwnOnly) {
                        companyOwnOnlyPermissions.add(p.CompanyPermissionCode as CompanyPermissionCode);
                    }
                }

                companiesPermissions[role.Company.Id] = companyPermissions;
                ownOnlyPermissions[role.Company.Id] = companyOwnOnlyPermissions;
            }

            isCustomer = isUserCustomer(user);
            for (const perm of allCompanyPermissions) {
                const isCustomerPermission = perm.CompanyRoleTypeCode === CompanyRoleTypeCode.CustomerRole;
                if (isCustomer === isCustomerPermission) {
                    availableCompanyPermissions.add(perm.Code as CompanyPermissionCode);
                }
            }

            const perms = user.GeneralRole.GeneralRolePermissions.filter((p: IGeneralRolePermissionEntity) => p.IsEnabled);
            for (const p of perms) {
                generalPermissions.add(p.PermissionCode as GeneralPermissionCode);
            }
        } catch (e) {
            logger.error(e.toString());
        }
        // check if permissions are correctly loaded, otherwise stay in loading state -> AppContext should handle
        // changing company or redirecting to login / tenant login
        this.setState({ companiesPermissions, generalPermissions, ownOnlyPermissions, isCustomer,  availableCompanyPermissions, isLoaded: true });
        this.props.onPermissionsLoaded(companiesPermissions, generalPermissions);
    };

    getContext = memoizeOne((): IPermissionContext => {
        return {
            companyPermissions: this.getCurrentCompanyPermissions(),
            ownOnlyPermissions: this.getCurrentOwnOnlyPermissions(),
            generalPermissions: this.state.generalPermissions,
            hasCompanyPermissions: this.hasCompanyPermissions,
            hasGeneralPermissions: this.hasGeneralPermissions,
            refreshUserPermissions: this.refreshUserPermissions,
            userIsCustomer: this.state.isCustomer
        };
    }, () => [this.state, this.context.getCompanyId()]);

    /* Fired when backend session changes (not login cookies session, but BE api/auth/session whatever that means)
    * => re-fetch permissions */
    handleSessionInvalidated = () => {
        this.refreshUserPermissions();
    };

    render() {
        // we have to render children to be able to redirect to login
        if (this.props.authContext?.isAuthenticated && !this.state.isLoaded && !(this.context as IAppContext).error) { //
            return <BusyIndicator size={BusyIndicatorSize.L} isDelayed/>;
        }

        return (
            <PermissionContext.Provider value={this.getContext()}>
                {this.props.children}
            </PermissionContext.Provider>
        );
    }
}

export default withAuthContext(withOData(PermissionContextProvider));